import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  Eraser,
  Code,
} from "lucide-react";

// script tags aur on* / javascript: attributes hata deta hai
function sanitizeHtml(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script, noscript").forEach((n) => n.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((a) => {
      const name = a.name.toLowerCase();
      const val = a.value.trim().toLowerCase();
      const isJs =
        (name === "href" || name === "src" || name === "xlink:href") &&
        val.startsWith("javascript:");
      if (name.startsWith("on") || isJs) el.removeAttribute(a.name);
    });
  });
  return doc.body.innerHTML;
}

function looksLikeHtml(text) {
  const t = (text || "").trim();
  return t.startsWith("<") && t.endsWith(">") && /<\/?[a-z][^>]*>/i.test(t);
}

export default function RichTextEditor({ value, onChange, placeholder }) {
  const [mode, setMode] = useState("visual"); // "visual" | "html"
  const areaRef = useRef(null);
  const lastEmitted = useRef(value);
  const savedRange = useRef(null);

  useEffect(() => {
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      /* ignore */
    }
  }, []);

  // value -> visual area (sirf tab jab bahar se badle ya visual tab khule)
  useEffect(() => {
    const el = areaRef.current;
    if (mode !== "visual" || !el) return;
    if (el.dataset.init !== "1" || value !== lastEmitted.current) {
      el.innerHTML = sanitizeHtml(value);
      el.dataset.init = "1";
      lastEmitted.current = value;
    }
  }, [value, mode]);

  function emit() {
    const el = areaRef.current;
    if (!el) return;
    const html = el.innerHTML;
    lastEmitted.current = html;
    onChange(html);
  }

  function exec(cmd, arg = null) {
    areaRef.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  }

  function saveSelection() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && areaRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }

  function restoreSelection() {
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  }

  function handleLink() {
    saveSelection();
    const url = window.prompt("Link URL (https://...)");
    if (!url) return;
    areaRef.current?.focus();
    restoreSelection();
    document.execCommand("createLink", false, url.trim());
    emit();
  }

  function handleImage() {
    saveSelection();
    const url = window.prompt("Image URL (https://...)");
    if (!url) return;
    areaRef.current?.focus();
    restoreSelection();
    document.execCommand("insertImage", false, url.trim());
    emit();
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData("text/plain");
    if (looksLikeHtml(text)) {
      // HTML code paste kiya hai -> raw text nahi, content ban ke aaye
      e.preventDefault();
      document.execCommand("insertHTML", false, sanitizeHtml(text.trim()));
      emit();
    }
  }

  const isVisual = mode === "visual";

  return (
    <div className="rte overflow-hidden rounded-lg border border-slate-300 bg-white focus-within:border-[#F5A623] focus-within:ring-1 focus-within:ring-[#F5A623]">
      {/* TOP BAR */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-2 py-2">
        <div
          className={`flex flex-wrap items-center gap-1 ${
            isVisual ? "" : "pointer-events-none opacity-40"
          }`}
        >
          <Btn title="Bold" onClick={() => exec("bold")}><Bold className="h-4 w-4" /></Btn>
          <Btn title="Italic" onClick={() => exec("italic")}><Italic className="h-4 w-4" /></Btn>
          <Btn title="Underline" onClick={() => exec("underline")}><Underline className="h-4 w-4" /></Btn>
          <Btn title="Strikethrough" onClick={() => exec("strikeThrough")}><Strikethrough className="h-4 w-4" /></Btn>

          <Sep />

          <Btn title="Paragraph" onClick={() => exec("formatBlock", "P")}><span className="text-xs font-bold">P</span></Btn>
          <Btn title="Heading 2" onClick={() => exec("formatBlock", "H2")}><span className="text-xs font-bold">H2</span></Btn>
          <Btn title="Heading 3" onClick={() => exec("formatBlock", "H3")}><span className="text-xs font-bold">H3</span></Btn>
          <Btn title="Heading 4" onClick={() => exec("formatBlock", "H4")}><span className="text-xs font-bold">H4</span></Btn>

          <Sep />

          <Btn title="Bullet list" onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></Btn>
          <Btn title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></Btn>
          <Btn title="Quote" onClick={() => exec("formatBlock", "BLOCKQUOTE")}><Quote className="h-4 w-4" /></Btn>
          <Btn title="Code block" onClick={() => exec("formatBlock", "PRE")}><Code className="h-4 w-4" /></Btn>

          <Sep />

          <Btn title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft className="h-4 w-4" /></Btn>
          <Btn title="Align center" onClick={() => exec("justifyCenter")}><AlignCenter className="h-4 w-4" /></Btn>
          <Btn title="Align right" onClick={() => exec("justifyRight")}><AlignRight className="h-4 w-4" /></Btn>

          <Sep />

          <Btn title="Insert link" onClick={handleLink}><LinkIcon className="h-4 w-4" /></Btn>
          <Btn title="Insert image (URL)" onClick={handleImage}><ImageIcon className="h-4 w-4" /></Btn>
          <Btn title="Clear formatting" onClick={() => exec("removeFormat")}><Eraser className="h-4 w-4" /></Btn>

          <Sep />

          <Btn title="Undo" onClick={() => exec("undo")}><Undo className="h-4 w-4" /></Btn>
          <Btn title="Redo" onClick={() => exec("redo")}><Redo className="h-4 w-4" /></Btn>
        </div>

        {/* MODE TABS */}
        <div className="ml-auto flex overflow-hidden rounded-md border border-slate-300 bg-white text-xs font-semibold">
          <button
            type="button"
            onClick={() => setMode("visual")}
            className={`px-3 py-1.5 ${isVisual ? "bg-[#F5A623] text-slate-950" : "text-slate-600 hover:bg-slate-50"}`}
          >
            Visual
          </button>
          <button
            type="button"
            onClick={() => setMode("html")}
            className={`px-3 py-1.5 ${!isVisual ? "bg-[#F5A623] text-slate-950" : "text-slate-600 hover:bg-slate-50"}`}
          >
            HTML Code
          </button>
        </div>
      </div>

      {/* EDITOR */}
      {isVisual ? (
        <div
          ref={areaRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onPaste={handlePaste}
          data-placeholder={placeholder || "Blog content likho ya HTML code paste karo..."}
          className="rte-content rte-area min-h-[320px] max-h-[70vh] overflow-y-auto p-4 text-slate-900 outline-none"
        />
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck="false"
          rows={16}
          placeholder="<h2>Heading</h2>&#10;<p>Yaha HTML code paste karo...</p>"
          className="block max-h-[70vh] min-h-[320px] w-full resize-y bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 outline-none placeholder:text-slate-500"
        />
      )}

      <style>{`
        .rte-area:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .rte-content { font-size: 0.95rem; line-height: 1.7; word-break: break-word; }
        .rte-content h1 { font-size: 1.9rem; font-weight: 800; margin: 1rem 0 0.5rem; }
        .rte-content h2 { font-size: 1.5rem; font-weight: 700; margin: 1rem 0 0.5rem; }
        .rte-content h3 { font-size: 1.25rem; font-weight: 700; margin: 0.9rem 0 0.4rem; }
        .rte-content h4 { font-size: 1.1rem; font-weight: 700; margin: 0.8rem 0 0.4rem; }
        .rte-content p { margin: 0.6rem 0; }
        .rte-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.6rem 0; }
        .rte-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.6rem 0; }
        .rte-content blockquote {
          border-left: 4px solid #F5A623; padding: 0.25rem 1rem; margin: 0.8rem 0;
          color: #475569; background: #f8fafc;
        }
        .rte-content pre {
          background: #0f172a; color: #e2e8f0; padding: 0.9rem 1rem; border-radius: 0.5rem;
          overflow-x: auto; font-family: ui-monospace, monospace; font-size: 0.8rem; margin: 0.8rem 0;
        }
        .rte-content code { font-family: ui-monospace, monospace; background: #f1f5f9; padding: 0.1rem 0.3rem; border-radius: 0.25rem; }
        .rte-content pre code { background: transparent; padding: 0; }
        .rte-content a { color: #d97706; text-decoration: underline; }
        .rte-content img { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 0.5rem 0; }
        .rte-content table { border-collapse: collapse; width: 100%; margin: 0.8rem 0; display: block; overflow-x: auto; }
        .rte-content th, .rte-content td { border: 1px solid #cbd5e1; padding: 0.4rem 0.6rem; }
        .rte-content th { background: #f1f5f9; font-weight: 700; }
      `}</style>
    </div>
  );
}

function Btn({ title, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()} // selection na hate
      onClick={onClick}
      className="flex h-8 min-w-[2rem] items-center justify-center rounded-md px-1.5 text-slate-700 transition hover:bg-slate-200"
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="mx-0.5 hidden h-5 w-px bg-slate-300 sm:block" />;
}